<template>
  <div class="pg-icomp">
    <!-- Import Script Section -->
    <div class="pg-icomp__section">
      <label class="pg-icomp__section-label">
        <i class="material-icons-round">file_download</i>
        Import from Sequence
      </label>
      <div class="pg-icomp__import">
        <div class="pg-icomp__import-row">
          <div class="p-field pg-icomp__import-field">
            <label class="p-field__label">Select Sequence Script</label>
            <select
              v-model="selectedSequenceScript"
              class="p-input"
              :disabled="availableSequenceScripts.length === 0"
            >
              <option :value="null">
                {{ availableSequenceScripts.length === 0 ? '순차통역 스크립트가 없습니다' : '기존 순차통역 스크립트 선택' }}
              </option>
              <option
                v-for="script in availableSequenceScripts"
                :key="script.id"
                :value="script"
              >
                UniqueKey: {{ script.uniqueKey }}
              </option>
            </select>
          </div>
          <div class="pg-icomp__import-action">
            <ABtn
              kind="ghost"
              icon="file_download"
              :disabled="!selectedSequenceScript || availableSequenceScripts.length === 0"
              @click.prevent="importSequenceScript"
            >
              Import Script
            </ABtn>
          </div>
        </div>
        <div class="pg-icomp__import-hint">
          <i class="material-icons-round">info</i>
          <span v-if="availableSequenceScripts.length > 0">
            기존 순차통역에서 만든 스크립트를 가져와서 대화완성 연습에 사용할 수 있습니다.
          </span>
          <span v-else>
            현재 교재에서 순차통역 스크립트를 찾을 수 없습니다. 먼저 순차통역을 작성해주세요.
          </span>
        </div>
      </div>
    </div>

    <!-- 화자 정보 설정 -->
    <div class="pg-icomp__section">
      <div class="pg-icomp__section-head">
        <label class="pg-icomp__section-label">
          <i class="material-icons-round">groups</i>
          Speaker Information
        </label>
        <ABtn kind="ghost" size="sm" icon="add" @click.prevent="addSpeaker">화자 추가</ABtn>
      </div>

      <div class="pg-icomp__speakers">
        <div
          v-for="(speaker, index) in sectionData.content.speakers"
          :key="index"
          class="pg-icomp__speaker"
        >
          <div class="pg-icomp__speaker-head">
            <div class="pg-icomp__speaker-title">
              <span class="pg-icomp__index">Speaker {{ index + 1 }}</span>
            </div>
            <button
              v-if="sectionData.content.speakers.length > 2"
              type="button"
              class="pg-icomp__icon-btn pg-icomp__icon-btn--danger"
              title="화자 삭제"
              @click.prevent="removeSpeaker(index)"
            >
              <i class="material-icons-round">delete</i>
            </button>
          </div>

          <div class="pg-icomp__speaker-body">
            <div class="p-form-grid p-form-grid--2">
              <div class="p-field">
                <label class="p-field__label">Speaker Name</label>
                <input
                  v-model="speaker.name"
                  type="text"
                  class="p-input"
                  placeholder="화자 이름 (예: 김철수, John)"
                />
              </div>
              <div class="p-field">
                <label class="p-field__label">Role/Title</label>
                <input
                  v-model="speaker.role"
                  type="text"
                  class="p-input"
                  placeholder="역할 (예: 의사, 환자, CEO)"
                />
              </div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-icomp__field-ico">language</i>
                Speaker Language
              </label>
              <select v-model="speaker.language" class="p-input">
                <option value="">언어 선택</option>
                <option value="ko">🇰🇷 한국어 (Korean)</option>
                <option value="en">🇺🇸 English</option>
                <option value="ja">🇯🇵 日本語 (Japanese)</option>
                <option value="zh">🇨🇳 中文 (Chinese)</option>
                <option value="vi">🇻🇳 Tiếng Việt (Vietnamese)</option>
                <option value="mn">🇲🇳 Монгол хэл (Mongolian)</option>
                <option value="ru">🇷🇺 Русский (Russian)</option>
              </select>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-icomp__field-ico">account_circle</i>
                Persona &amp; Background
              </label>
              <textarea
                v-model="speaker.persona"
                class="p-input p-input--area"
                rows="3"
                placeholder="화자의 성격, 배경, 말하는 스타일 등을 설명하세요..."
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 대화 스크립트 -->
    <div class="pg-icomp__section">
      <label class="pg-icomp__section-label">
        <i class="material-icons-round">description</i>
        Conversation Script
      </label>

      <div class="pg-icomp__scripts">
        <div
          v-for="(script, index) in sectionData.content.scripts"
          :key="index"
          class="pg-icomp__script"
        >
          <div class="pg-icomp__script-head">
            <div class="pg-icomp__script-info">
              <span class="pg-icomp__index">{{ index + 1 }}</span>
              <span
                class="pg-icomp__speaker-pill"
                :class="getSpeakerBadgeClass(script.speakerId)"
              >
                <i class="material-icons-round">person</i>
                {{ getSpeakerInfo(script.speakerId) }}
              </span>
            </div>
            <div class="pg-icomp__script-actions">
              <button
                type="button"
                class="pg-icomp__icon-btn"
                title="위로 이동"
                :disabled="index === 0"
                @click.prevent="moveScriptUp(index)"
              >
                <i class="material-icons-round">arrow_upward</i>
              </button>
              <button
                type="button"
                class="pg-icomp__icon-btn"
                title="아래로 이동"
                :disabled="index === sectionData.content.scripts.length - 1"
                @click.prevent="moveScriptDown(index)"
              >
                <i class="material-icons-round">arrow_downward</i>
              </button>
              <button
                type="button"
                class="pg-icomp__icon-btn pg-icomp__icon-btn--danger"
                title="Delete script"
                @click.prevent="removeScript(index)"
              >
                <i class="material-icons-round">delete</i>
              </button>
            </div>
          </div>

          <div class="pg-icomp__script-body">
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-icomp__field-ico">mic</i>
                Original Text
              </label>
              <textarea
                v-model="script.originalText"
                class="p-input p-input--area"
                rows="3"
                placeholder="화자가 말하는 원문을 입력하세요..."
                @input="updateWordCount(script)"
              />
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-icomp__field-ico pg-icomp__field-ico--audio">music_note</i>
                Original Audio URL
              </label>
              <div class="pg-icomp__audio-row">
                <input
                  v-model="script.audioUrl"
                  type="url"
                  class="p-input"
                  placeholder="원문 오디오 파일의 URL을 입력하세요 (예: https://example.com/audio.mp3)..."
                />
                <button
                  v-if="script.audioUrl"
                  type="button"
                  class="pg-icomp__icon-btn pg-icomp__icon-btn--audio"
                  :title="isPlaying(index) ? '오디오 중지' : '오디오 재생'"
                  @click.prevent="toggleAudio(script.audioUrl, index)"
                >
                  <i class="material-icons-round">{{ isPlaying(index) ? 'stop' : 'play_arrow' }}</i>
                </button>
              </div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-icomp__field-ico">translate</i>
                Translation
              </label>
              <textarea
                v-model="script.translation"
                class="p-input p-input--area"
                rows="3"
                placeholder="통역해야 할 번역문을 입력하세요..."
              />
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-icomp__field-ico pg-icomp__field-ico--warn">music_note</i>
                Translation Audio URL
              </label>
              <div class="pg-icomp__audio-row">
                <input
                  v-model="script.translationAudioUrl"
                  type="url"
                  class="p-input"
                  placeholder="번역문 오디오 파일의 URL을 입력하세요 (예: https://example.com/translation.mp3)..."
                />
                <button
                  v-if="script.translationAudioUrl"
                  type="button"
                  class="pg-icomp__icon-btn pg-icomp__icon-btn--audio"
                  :title="isTranslationPlaying(index) ? '오디오 중지' : '오디오 재생'"
                  @click.prevent="toggleTranslationAudio(script.translationAudioUrl, index)"
                >
                  <i class="material-icons-round">{{ isTranslationPlaying(index) ? 'stop' : 'play_arrow' }}</i>
                </button>
              </div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-icomp__field-ico pg-icomp__field-ico--ok">volume_up</i>
                Pronunciation Script
              </label>
              <textarea
                v-model="script.pronunciation"
                class="p-input p-input--area"
                rows="2"
                placeholder="번역문의 발음을 한글로 표기하세요 (예: '맛있다' → '마시따' 또는 '마지따')..."
              />
            </div>

            <div class="pg-icomp__settings">
              <div class="p-form-grid p-form-grid--4">
                <div class="p-field">
                  <label class="p-field__label">Difficulty</label>
                  <select v-model="script.difficulty" class="p-input">
                    <option value="easy">🟢 쉬움 (Easy)</option>
                    <option value="medium">🟡 보통 (Medium)</option>
                    <option value="hard">🔴 어려움 (Hard)</option>
                  </select>
                </div>
                <div class="p-field">
                  <label class="p-field__label">Duration (sec)</label>
                  <input
                    v-model.number="script.duration"
                    type="number"
                    class="p-input"
                    min="5"
                    max="120"
                    placeholder="소요시간"
                  />
                </div>
                <div class="p-field">
                  <label class="p-field__label">Word Count</label>
                  <div class="pg-icomp__word-count">
                    <span class="pg-icomp__word-count-pill">{{ script.wordCount || 0 }} words</span>
                  </div>
                </div>
                <div class="p-field">
                  <label class="p-field__label">Context Type</label>
                  <select v-model="script.contextType" class="p-input">
                    <option value="formal">🎩 격식 (Formal)</option>
                    <option value="informal">😊 비격식 (Informal)</option>
                    <option value="technical">⚙️ 전문 (Technical)</option>
                    <option value="emotional">💭 감정적 (Emotional)</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-icomp__field-ico">sticky_note_2</i>
                Notes &amp; Context
              </label>
              <textarea
                v-model="script.notes"
                class="p-input p-input--area"
                rows="2"
                placeholder="통역 시 주의사항이나 맥락 정보를 입력하세요..."
              />
            </div>
          </div>
        </div>

        <!-- 새 스크립트 추가 -->
        <div class="pg-icomp__add">
          <div class="pg-icomp__add-row">
            <div class="p-field pg-icomp__add-field">
              <label class="p-field__label">Select Speaker</label>
              <select v-model="newScriptSpeaker" class="p-input">
                <option value="">화자 선택</option>
                <option
                  v-for="speaker in sectionData.content.speakers"
                  :key="speaker.id"
                  :value="speaker.id"
                >
                  {{ speaker.name || speaker.id }} ({{ getLanguageName(speaker.language) }})
                </option>
              </select>
            </div>
            <div class="pg-icomp__add-action">
              <ABtn
                kind="yellow"
                icon="add"
                :disabled="newScriptSpeaker === ''"
                @click.prevent="addScript"
              >
                Add Script
              </ABtn>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 대화 통계 -->
    <div class="pg-icomp__section">
      <label class="pg-icomp__section-label">
        <i class="material-icons-round">bar_chart</i>
        Conversation Statistics
      </label>
      <div class="pg-icomp__stats">
        <div class="pg-icomp__stat">
          <div class="pg-icomp__stat-num">{{ sectionData.content.scripts.length }}</div>
          <div class="pg-icomp__stat-label">Total Scripts</div>
        </div>
        <div class="pg-icomp__stat">
          <div class="pg-icomp__stat-num">{{ getTotalDuration() }}s</div>
          <div class="pg-icomp__stat-label">Total Duration</div>
        </div>
        <div class="pg-icomp__stat">
          <div class="pg-icomp__stat-num">{{ getTotalWords() }}</div>
          <div class="pg-icomp__stat-label">Total Words</div>
        </div>
        <div class="pg-icomp__stat">
          <div class="pg-icomp__stat-num">{{ getScriptsByLanguage('ko') }}/{{ getScriptsByLanguage('en') }}</div>
          <div class="pg-icomp__stat-label">KO/EN Scripts</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineModel, watch, ref, onMounted, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import CONF from "@/Config.js";
import ABtn from "@/components/Pineapple/ABtn.vue";

const sectionData = defineModel({ type: Object, required: true });
const newScriptSpeaker = ref('');
const playingAudioIndex = ref(null);
const currentAudio = ref(null);
const playingTranslationAudioIndex = ref(null);
const currentTranslationAudio = ref(null);
const selectedSequenceScript = ref(null);
const availableSequenceScripts = ref([]);

// Composition API에서 Vue Router와 notify 접근
const route = useRoute();
const instance = getCurrentInstance();
const { $notify } = instance?.appContext.config.globalProperties || {};

// props 정의 (부모 컴포넌트에서 sections 데이터 전달받기)
const props = defineProps({
  sections: {
    type: Array,
    required: false,
    default: () => []
  },
  chapterNumber: {
    type: Number,
    required: false,
    default: null
  }
});

// 언어 이름 매핑
const languageNames = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  zh: '中文',
  vi: 'Tiếng Việt',
  mn: 'Монгол хэл',
  ru: 'Русский'
};

const getLanguageName = (languageCode) => {
  return languageNames[languageCode] || languageCode;
};

// 단어 수 계산
const updateWordCount = (script) => {
  if (script.originalText) {
    const words = script.originalText.trim().split(/\s+/).filter(word => word.length > 0);
    script.wordCount = words.length;
  } else {
    script.wordCount = 0;
  }
};

// 화자 정보 가져오기
const getSpeakerInfo = (speakerId) => {
  // 새로운 문자열 ID로 찾기
  let speaker = sectionData.value.content.speakers.find(s => s.id === speakerId);

  // 기존 숫자 형태라면 인덱스로 찾기
  if (!speaker && typeof speakerId === 'number') {
    speaker = sectionData.value.content.speakers[speakerId];
  }

  if (!speaker) return `Unknown Speaker`;

  const name = speaker.name || speaker.id || `Speaker ${speakerId}`;
  const language = getLanguageName(speaker.language);
  return `${name} (${language})`;
};

// 화자 배지 CSS 클래스 가져오기
const getSpeakerBadgeClass = (speakerId) => {
  // speaker_1 -> speaker-1, speaker_2 -> speaker-2
  if (speakerId === 'speaker_1') return 'speaker-1';
  if (speakerId === 'speaker_2') return 'speaker-2';

  // 기존 숫자 형태 지원 (0 -> speaker-1, 1 -> speaker-2)
  if (speakerId === 0 || speakerId === '0') return 'speaker-1';
  if (speakerId === 1 || speakerId === '1') return 'speaker-2';

  return 'speaker-default';
};

// 통계 함수들
const getTotalDuration = () => {
  return sectionData.value.content.scripts.reduce((total, script) => total + (script.duration || 0), 0);
};

const getTotalWords = () => {
  return sectionData.value.content.scripts.reduce((total, script) => total + (script.wordCount || 0), 0);
};

const getScriptsByLanguage = (languageCode) => {
  return sectionData.value.content.scripts.filter(script => {
    // 새로운 문자열 ID로 찾기
    let speaker = sectionData.value.content.speakers.find(s => s.id === script.speakerId);

    // 기존 숫자 형태라면 인덱스로 찾기
    if (!speaker && typeof script.speakerId === 'number') {
      speaker = sectionData.value.content.speakers[script.speakerId];
    }

    return speaker && speaker.language === languageCode;
  }).length;
};

// 기존 순차통역 스크립트 불러오기 (부모에서 전달받은 sections 데이터 활용)
const loadAvailableSequenceScripts = async () => {
  try {

    // 부모 컴포넌트에서 전달받은 sections 데이터 활용
    if (!props.sections || props.sections.length === 0) {
      console.log('부모 컴포넌트에서 sections 데이터를 받지 못했습니다. Import 기능을 비활성화합니다.');
      availableSequenceScripts.value = [];
      return;
    }

    const barcode = route.query.barcode;
    if (!barcode) {
      console.warn('교재 정보(barcode)를 찾을 수 없습니다.');
      availableSequenceScripts.value = [];
      return;
    }

    // 부모에서 받은 sections 중에서 순차통역만 필터링
    const sequenceSections = props.sections
      .filter(item => item.type === 'practice' && item.subType === 'InterpretingConversationSequence');

    // 각 순차통역 섹션의 상세 정보를 가져오기
    const sequenceScripts = [];

    for (const section of sequenceSections) {
      sequenceScripts.push(section);
    }

    availableSequenceScripts.value = sequenceScripts;
    console.log(`현재 챕터에서 순차통역 스크립트 ${sequenceScripts.length}개를 찾았습니다.`);

  } catch (error) {
    console.warn('순차통역 스크립트 로드 실패:', error.message);
    availableSequenceScripts.value = [];
  }
};

// 순차통역 스크립트 가져오기 (SectionsList.vue의 loadSection 방식 참조)
const importSequenceScript = async () => {
  if (!selectedSequenceScript.value) return;

  const selectedSection = selectedSequenceScript.value;
  //console.log('선택된 섹션:', selectedSection);

  try {
    const barcode = route.query.barcode;

    if (!barcode) {
      alert('교재 정보를 찾을 수 없습니다.');
      return;
    }

    const currentChapterNumber = props.chapterNumber;

    // 인증 토큰 가져오기
    const token = localStorage.getItem("access_token");

    // SectionsList.vue의 loadSection과 동일한 방식으로 상세 데이터 가져오기
    const res = await axios.get(
      `${CONF.API_URL}/textbook/textbookContents/${barcode}/${currentChapterNumber}/${selectedSection.uniqueKey}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    //console.log('API 응답:', res);

    if (res.status === 200) {
      const sequenceDetailData = res.data;
      //console.log('순차통역 상세 데이터:', sequenceDetailData);
      //console.log('API에서 받은 speakers:', sequenceDetailData.content?.speakers);
      //console.log('API에서 받은 scripts:', sequenceDetailData.content?.scripts);

      // scripts와 speakers가 있는지 확인
      if (!sequenceDetailData.content?.scripts || sequenceDetailData.content.scripts.length === 0) {
        alert('선택한 순차통역에 스크립트 데이터가 없습니다.');
        return;
      }

      if (!sequenceDetailData.content?.speakers || sequenceDetailData.content.speakers.length === 0) {
        alert('선택한 순차통역에 화자 데이터가 없습니다.');
        return;
      }

      // 현재 섹션의 speakers 배열 확인 및 초기화
      if (!sectionData.value.content.speakers) {
        sectionData.value.content.speakers = [];
      }

      //console.log('현재 섹션의 기존 speakers:', sectionData.value.content.speakers);

      // 사용자에게 기존 화자 정보 덮어쓰기 확인
      const hasExistingSpeakersWithData = sectionData.value.content.speakers.some(speaker =>
        speaker.name?.trim() || speaker.role?.trim() || speaker.language?.trim() || speaker.persona?.trim()
      );

      let shouldOverwrite = true;
      if (hasExistingSpeakersWithData) {
        shouldOverwrite = confirm('기존에 작성된 화자 정보가 있습니다. Import한 화자 정보로 덮어쓰시겠습니까?\n\n확인: 덮어쓰기\n취소: 새로운 화자만 추가');
      }

      if (shouldOverwrite) {
        // 기존 화자 정보를 Import 데이터로 완전 대체
        console.log('기존 화자 정보를 완전 대체합니다.');
        sectionData.value.content.speakers = [...sequenceDetailData.content.speakers];
      } else {
        // 기존 방식: 새로운 화자만 추가
        const existingSpeakerIds = sectionData.value.content.speakers.map(s => s.id);
        //console.log('기존 화자 ID들:', existingSpeakerIds);

        const newSpeakers = sequenceDetailData.content.speakers.filter(speaker =>
          !existingSpeakerIds.includes(speaker.id)
        );

        //console.log('새로 추가될 speakers:', newSpeakers);

        // speakers 추가 (새로운 화자만)
        sectionData.value.content.speakers = [
          ...sectionData.value.content.speakers,
          ...newSpeakers
        ];
      }

      // scripts 추가 (scripts 배열 초기화 확인)
      if (!sectionData.value.content.scripts) {
        sectionData.value.content.scripts = [];
      }

      sectionData.value.content.scripts = [
        ...sectionData.value.content.scripts,
        ...sequenceDetailData.content.scripts
      ];

      // 선택 초기화
      selectedSequenceScript.value = null;

      /*
      // 통계 계산
      const importedSpeakersCount = sequenceDetailData.content.speakers.length;
      const newScriptsCount = sequenceDetailData.content.scripts.length;

      console.log('최종 speakers:', sectionData.value.content.speakers);
      console.log('최종 scripts:', sectionData.value.content.scripts);

      const actionType = shouldOverwrite ? '덮어쓰기' : '추가';
      const message = `성공적으로 가져왔습니다!\n- 화자 ${importedSpeakersCount}명 ${actionType}\n- 스크립트 ${newScriptsCount}개 추가\n- 출처: Section ${sequenceDetailData.sectionNumber}`;

      console.log('Import 완료:', {
        importedSpeakersCount,
        newScriptsCount,
        actionType,
        source: `Section ${sequenceDetailData.sectionNumber}`,
        totalSpeakers: sectionData.value.content.speakers.length,
        totalScripts: sectionData.value.content.scripts.length
      });
      */

    } else {
      $notify({
        title: `데이터를 가져오지 못했습니다. (상태코드: ${res.status})`,
        type: 'error'
      });
    }

  } catch (error) {
    console.error('순차통역 스크립트 가져오기 실패:', error);
    alert('스크립트를 가져오는 중 오류가 발생했습니다. 다시 시도해주세요.');
  }
};

// 컴포넌트 마운트 시 순차통역 스크립트 로드
onMounted(() => {
  loadAvailableSequenceScripts();
});

// 화자 추가 함수
const addSpeaker = () => {
  const currentSpeakers = sectionData.value.content.speakers;
  const newSpeakerNumber = currentSpeakers.length + 1;
  const newSpeakerId = `speaker_${newSpeakerNumber}`;

  currentSpeakers.push({
    id: newSpeakerId,
    name: '',
    role: '',
    language: '',
    persona: ''
  });
};

// 화자 삭제 함수 (최소 2명 유지)
const removeSpeaker = (index) => {
  if (sectionData.value.content.speakers.length <= 2) {
    alert('최소 2명의 화자가 필요합니다.');
    return;
  }

  const speakerToRemove = sectionData.value.content.speakers[index];

  // 해당 화자를 사용하는 스크립트가 있는지 확인
  const hasScripts = sectionData.value.content.scripts.some(
    script => script.speakerId === speakerToRemove.id
  );

  if (hasScripts) {
    const confirmDelete = confirm(`${speakerToRemove.name || speakerToRemove.id}를 사용하는 스크립트가 있습니다. 정말 삭제하시겠습니까?`);
    if (!confirmDelete) return;

    // 해당 화자를 사용하는 스크립트도 함께 삭제
    sectionData.value.content.scripts = sectionData.value.content.scripts.filter(
      script => script.speakerId !== speakerToRemove.id
    );
  }

  sectionData.value.content.speakers.splice(index, 1);
};

// 데이터 구조 초기화
watch(() => sectionData.value.content, (newContent) => {
  if (!newContent.speakers) {
    sectionData.value.content.speakers = [
      { id: 'speaker_1', name: '', role: '', language: '', persona: '' },
      { id: 'speaker_2', name: '', role: '', language: '', persona: '' }
    ];
  } else {
    // 기존 speakers에 id가 없다면 추가
    sectionData.value.content.speakers.forEach((speaker, index) => {
      if (!speaker.id) {
        speaker.id = `speaker_${index + 1}`;
      }
    });
  }
  if (!newContent.scripts) {
    sectionData.value.content.scripts = [];
  } else {
    // 기존 scripts의 speakerId가 숫자라면 문자열로 변환
    sectionData.value.content.scripts.forEach(script => {
      if (typeof script.speakerId === 'number') {
        script.speakerId = `speaker_${script.speakerId + 1}`;
      }
    });
  }
}, { immediate: true, deep: true });

const addScript = () => {
  if (newScriptSpeaker.value === '') return;

  sectionData.value.content.scripts.push({
    speakerId: newScriptSpeaker.value,
    originalText: '',
    audioUrl: '',
    translation: '',
    translationAudioUrl: '',
    pronunciation: '',
    difficulty: 'medium',
    duration: 30,
    contextType: 'formal',
    notes: '',
    wordCount: 0
  });

  newScriptSpeaker.value = '';
};

// 오디오 재생 상태 확인
const isPlaying = (index) => {
  return playingAudioIndex.value === index;
};

// 오디오 토글 함수 (재생/중지)
const toggleAudio = async (audioUrl, index) => {
  if (!audioUrl) return;

  // 현재 재생 중인 오디오가 있다면 중지
  if (playingAudioIndex.value === index) {
    stopAudio();
    return;
  }

  // 다른 오디오가 재생 중이라면 중지
  if (currentAudio.value) {
    currentAudio.value.pause();
    currentAudio.value = null;
  }

  try {
    const audio = new Audio(audioUrl);

    // 오디오 이벤트 리스너 추가
    audio.addEventListener('ended', () => {
      playingAudioIndex.value = null;
      currentAudio.value = null;
    });

    audio.addEventListener('error', (error) => {
      console.error('오디오 재생 실패:', error);
      alert('오디오 파일을 재생할 수 없습니다. URL을 확인해주세요.');
      playingAudioIndex.value = null;
      currentAudio.value = null;
    });

    await audio.play();
    currentAudio.value = audio;
    playingAudioIndex.value = index;

  } catch (error) {
    console.error('오디오 재생 오류:', error);
    alert('오디오 파일을 재생할 수 없습니다.');
    playingAudioIndex.value = null;
    currentAudio.value = null;
  }
};

// 오디오 중지 함수
const stopAudio = () => {
  if (currentAudio.value) {
    currentAudio.value.pause();
    currentAudio.value = null;
  }
  playingAudioIndex.value = null;
};

// 번역문 오디오 재생 상태 확인
const isTranslationPlaying = (index) => {
  return playingTranslationAudioIndex.value === index;
};

// 번역문 오디오 토글 함수 (재생/중지)
const toggleTranslationAudio = async (audioUrl, index) => {
  if (!audioUrl) return;

  // 현재 재생 중인 번역문 오디오가 있다면 중지
  if (playingTranslationAudioIndex.value === index) {
    stopTranslationAudio();
    return;
  }

  // 다른 번역문 오디오가 재생 중이라면 중지
  if (currentTranslationAudio.value) {
    currentTranslationAudio.value.pause();
    currentTranslationAudio.value = null;
  }

  try {
    const audio = new Audio(audioUrl);

    // 오디오 이벤트 리스너 추가
    audio.addEventListener('ended', () => {
      playingTranslationAudioIndex.value = null;
      currentTranslationAudio.value = null;
    });

    audio.addEventListener('error', (error) => {
      console.error('번역문 오디오 재생 실패:', error);
      alert('번역문 오디오 파일을 재생할 수 없습니다. URL을 확인해주세요.');
      playingTranslationAudioIndex.value = null;
      currentTranslationAudio.value = null;
    });

    await audio.play();
    currentTranslationAudio.value = audio;
    playingTranslationAudioIndex.value = index;

  } catch (error) {
    console.error('번역문 오디오 재생 오류:', error);
    alert('번역문 오디오 파일을 재생할 수 없습니다.');
    playingTranslationAudioIndex.value = null;
    currentTranslationAudio.value = null;
  }
};

// 번역문 오디오 중지 함수
const stopTranslationAudio = () => {
  if (currentTranslationAudio.value) {
    currentTranslationAudio.value.pause();
    currentTranslationAudio.value = null;
  }
  playingTranslationAudioIndex.value = null;
};

const removeScript = (index) => {
  sectionData.value.content.scripts.splice(index, 1);
};

const moveScriptUp = (index) => {
  if (index === 0) return;
  const scripts = sectionData.value.content.scripts;
  const temp = scripts[index];
  scripts[index] = scripts[index - 1];
  scripts[index - 1] = temp;
};

const moveScriptDown = (index) => {
  const scripts = sectionData.value.content.scripts;
  if (index === scripts.length - 1) return;
  const temp = scripts[index];
  scripts[index] = scripts[index + 1];
  scripts[index + 1] = temp;
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-icomp {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__section-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  &__section-label {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid $p-line;
    letter-spacing: 0.02em;

    > i {
      font-size: 16px;
      color: $p-ink-mid;
    }
  }

  &__index {
    padding: 4px 12px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
    display: inline-block;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;
    flex-shrink: 0;

    > i { font-size: 16px; }

    &:hover:not(:disabled) {
      background: $p-panel-alt;
      color: $p-ink;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &--danger {
      color: $p-err;

      &:hover:not(:disabled) {
        background: $p-err-soft;
        color: $p-err;
      }
    }

    &--audio {
      color: $p-info;

      &:hover:not(:disabled) {
        background: $p-panel-alt;
        color: $p-info;
      }
    }
  }

  &__field-ico {
    font-size: 14px !important;
    color: $p-ink-mid;
    vertical-align: middle;

    &--audio { color: $p-info; }
    &--warn { color: $p-warn; }
    &--ok { color: $p-ok; }
  }

  /* Import */
  &__import {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__import-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__import-field { flex: 1; }

  &__import-action {
    display: flex;
    align-items: flex-end;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  &__import-hint {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 10px 12px;
    background: $p-panel-alt;
    border-left: 3px solid $p-ok;
    border-radius: $p-radius-sm;
    color: $p-ink-soft;
    font-size: 12px;
    line-height: 1.5;

    > i {
      font-size: 14px;
      color: $p-ok;
      flex-shrink: 0;
      margin-top: 1px;
    }
  }

  /* Speakers */
  &__speakers {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__speaker {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    overflow: hidden;
    transition: border-color $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }
  }

  &__speaker-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: $p-panel-alt;
    border-bottom: 1px solid $p-line;
  }

  &__speaker-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__speaker-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px;
  }

  /* Scripts */
  &__scripts {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__script {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    overflow: hidden;
    transition: border-color $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }
  }

  &__script-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: $p-panel-alt;
    border-bottom: 1px solid $p-line;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__script-info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }

  &__script-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  &__script-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px;
  }

  &__speaker-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    background: $p-panel-alt;
    color: $p-ink-mid;
    border: 1px solid $p-line;

    > i { font-size: 12px; }

    &.speaker-1 {
      background: $p-point-soft;
      color: $p-point-deep;
      border-color: $p-point;
    }

    &.speaker-2 {
      background: rgba($p-ok, 0.12);
      color: $p-ok;
      border-color: rgba($p-ok, 0.4);
    }

    &.speaker-default {
      background: $p-panel-alt;
      color: $p-ink-soft;
    }
  }

  &__audio-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .p-input { flex: 1; }
  }

  &__settings {
    background: $p-panel-alt;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    padding: 12px;
  }

  &__word-count {
    display: flex;
    align-items: center;
    height: 38px;
  }

  &__word-count-pill {
    background: $p-ink;
    color: $p-point;
    padding: 6px 12px;
    border-radius: 999px;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  /* Add Script */
  &__add {
    background: $p-panel-alt;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-md;
    padding: 14px 16px;
  }

  &__add-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__add-field { flex: 1; }

  &__add-action {
    display: flex;
    align-items: flex-end;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  /* Stats */
  &__stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &__stat {
    text-align: center;
    padding: 14px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
  }

  &__stat-num {
    font-family: $p-font-mono;
    font-size: 22px;
    font-weight: 800;
    color: $p-point-deep;
    margin-bottom: 4px;
    letter-spacing: 0.02em;
  }

  &__stat-label {
    font-size: 11px;
    font-weight: 600;
    color: $p-ink-soft;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
}
</style>
